import { Decimal } from 'decimal.js';
import { PERCENT_DP } from './constants';
import { fromDecimal, roundTo, toDecimal } from './money';
import type { Holding, ValuedHolding } from './types';

/**
 * สัดส่วนของพอร์ต สำหรับวาดกราฟวงกลม
 *
 * นับเฉพาะรายการที่ตีมูลค่าเป็นบาทได้ — รายการที่ยังไม่มีราคาไม่มีทางรู้ว่าควรกินพื้นที่เท่าไหร่
 * จึงไม่เดาแล้วใส่ลงไป แต่รายงานจำนวนไว้ใน `excludedCount` ให้ UI บอกผู้ใช้ได้
 */

export interface AllocationSlice {
  key: string;
  valueTHB: string;
  /** เปอร์เซ็นต์ของพอร์ต — null เมื่อมูลค่ารวมเป็นศูนย์ (หารไม่ได้) */
  sharePct: string | null;
}

export interface Allocation {
  /** เรียงจากมูลค่ามากไปน้อย เพื่อให้กราฟอ่านง่ายและลำดับคงที่ */
  slices: AllocationSlice[];
  totalTHB: string;
  /** จำนวนรายการที่ตีมูลค่าไม่ได้ จึงไม่ได้อยู่ในกราฟ */
  excludedCount: number;
}

export function buildAllocation(
  items: readonly ValuedHolding[],
  keyOf: (holding: Holding) => string,
): Allocation {
  const totals = new Map<string, Decimal>();
  let total = new Decimal(0);
  let excludedCount = 0;

  for (const { holding, valuation } of items) {
    if (valuation.marketValueTHB === null) {
      excludedCount += 1;
      continue;
    }

    const value = toDecimal(valuation.marketValueTHB, 'valuation.marketValueTHB');
    const key = keyOf(holding);
    totals.set(key, (totals.get(key) ?? new Decimal(0)).plus(value));
    total = total.plus(value);
  }

  const slices = [...totals.entries()]
    .map(([key, value]) => ({
      key,
      valueTHB: fromDecimal(value),
      sharePct: total.isZero() ? null : roundTo(value.div(total).mul(100), PERCENT_DP),
    }))
    // เรียงมาก→น้อย แล้วเรียงตามชื่อเมื่อมูลค่าเท่ากัน เพื่อให้ผลลัพธ์คงที่ทุกครั้ง
    .sort((a, b) => {
      const diff = toDecimal(b.valueTHB).comparedTo(toDecimal(a.valueTHB));
      return diff !== 0 ? diff : a.key.localeCompare(b.key);
    });

  return { slices, totalTHB: fromDecimal(total), excludedCount };
}
